<template>
  <div class="radio-list-item-parts" :class="`radio-list-item-parts--${screenOrientationType}`">
    <radio-list-play-button-parts
      :screenOrientationType="screenOrientationType"
      :radioData="playerRadioData"
      @playRadio="playRadioAudio(playerRadioData, false)"
    />

    <button
      class="radio-list-item-parts__video-scene-select"
      @click="playRadioAudio(playerRadioData, true)"
    >
      <span class="radio-list-item-parts__video-scene-select__img"
        ><img :src="playerRadioData.playerPictureImagePath" alt=""
      /></span>

      <span class="radio-list-item-parts__video-scene-select__information">
        <span class="radio-list-item-parts__video-scene-select__name">
          <span class="name"
            >#{{ playerRadioData.car_no }} {{ playerRadioData.playerName[$i18n.locale] }}</span
          >
          <span class="abbreviation"
            >#{{ playerRadioData.car_no }} {{ playerRadioData.abbreviation[$i18n.locale] }}</span
          >
        </span>
        <span class="radio-list-item-parts__video-scene-select__lap-data"
          >Lap {{ lap }}・{{ sector }}</span
        >
      </span>
      <svg
        class="radio-list-item-parts__video-scene-select__icon"
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M2 2.25C0.895431 2.25 0 3.14543 0 4.25V13.75C0 14.8546 0.89543 15.75 2 15.75H16C17.1046 15.75 18 14.8546 18 13.75V4.25C18 3.14543 17.1046 2.25 16 2.25H2ZM12.75 9L6.75 5.25V12.75L12.75 9ZM1.50002 3.75014H3.00002V5.25014H1.50002V3.75014ZM3.00002 6.75014H1.50002V8.25014H3.00002V6.75014ZM1.50002 9.75014H3.00002V11.2501H1.50002V9.75014ZM3.00002 12.7501H1.50002V14.2501H3.00002V12.7501ZM15 3.75H16.5V5.25H15V3.75ZM16.5 6.75H15V8.25H16.5V6.75ZM15 9.75H16.5V11.25H15V9.75ZM16.5 12.75H15V14.25H16.5V12.75Z"
          fill="white"
        />
      </svg>
    </button>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from '@vue/composition-api'
import RadioListPlayButtonParts from '@/components/RaceVideoPage/common/parts/RadioListPlayButtonParts.vue'
import RadioDataDocument from '@/store/stores/collectionModule/documents/Radio/RadioDataDocument'

export default defineComponent({
  name: 'RadioListItemParts',
  components: {
    RadioListPlayButtonParts,
  },
  props: {
    /**
     * 画面向き
     */
    screenOrientationType: {
      type: String,
      default: 'portrait-primary',
    },
    /**
     * 選手無線データ
     */
    radioData: {
      type: Object as PropType<RadioDataDocument>,
      require: true,
    },
  },
  setup(props) {
    const playerRadioData = computed(() => props.radioData)
    const lap = computed(() => props.radioData?.lap)
    const sector = computed(() => props.radioData?.sector)
    return {
      playerRadioData,
      lap,
      sector,
    }
  },
  methods: {
    /**
     * 無線交信データを再生する。
     */
    async playRadioAudio(radioData: RadioDataDocument, scene: boolean) {
      this.$emit('radioPlay', radioData, scene)
    },
  },
})
</script>

<style lang="scss" scoped>
.radio-list-item-parts {
  font-size: min(3.2vw, calc(var(--screen-height) * 0.0217));
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 8px;
  background-color: $black20;
  border-radius: 4px;

  &::before {
    position: absolute;
    top: -8px;
    left: calc(50% - 1px);
    content: '';
    width: 2px;
    height: 8px;
    background-color: #e5e5e5;
  }

  &__video-scene-select {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: calc(100% - 94px);
    height: 36px;
    padding: 0 12px;

    &__img {
      width: 32px;
    }

    &__information {
      display: block;
      width: calc(100% - 70px);
      padding-right: 4px;
    }

    &__name {
      display: block;
      @include fontSetTextMedium();

      font-weight: 600;
      color: $white;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;

      .name {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;

        @media screen and (max-width: 374px) {
          display: none;
        }
      }

      .abbreviation {
        display: none;
        overflow: hidden;
        text-overflow: ellipsis;

        @media screen and (max-width: 374px) {
          display: block;
        }
      }
    }

    &__lap-data {
      display: block;
      @include fontSetTextSmall();

      color: $gray25;
    }

    &__icon {
      width: 18px;
    }
  }

  @media screen and (max-width: 374px) {
    .radio-list-item-parts {
      &__video-scene-select {
        width: calc(100% - 66px);
        padding: 0 8px;
        gap: 4px;

        &__img {
          width: 24px;
        }

        &__information {
          width: calc(100% - 58px);
        }

        &__lap-data {
          font-size: 8px;
          line-height: 1.5;
        }
      }
    }
  }

  &--landscape-primary,
  &--landscape-secondary {
    &:first-child {
      margin-top: 8px;

      &::before {
        top: -8px;
        height: 8px;
      }
    }

    .radio-list-item-parts {
      &__video-scene-select {
        width: calc(100% - 66px);
        padding: 0 8px;
        gap: 4px;

        &__img {
          width: 24px;
        }

        &__name {
          .name {
            display: none;
          }

          .abbreviation {
            display: block;
          }
        }

        &__information {
          width: calc(100% - 58px);
        }

        &__lap-data {
          font-size: 8px;
          line-height: 1.5;
        }
      }
    }
  }
}
</style>
